<script lang="ts" setup>
import MLwChart from '@/components/charts/MLwChart.vue'
import { useAutoRefresh } from '@/composable/useAutorefresh'
import Button from 'primevue/button'
import { useWebSocket } from '@/composable/useWebsockets'
import type { CexTickerResponse, GmgnPriceResponse } from '@/services/dex-pools-v2-service/types'
import { useAssetsStore } from '@/stores/assetsStore'
import Card from 'primevue/card'
import { useAuthStore } from '@/stores/authStore'
import { useEyeStore } from '@/stores/eyeStore'
import { useThemeStore } from '@/stores/themeStore'
import type { WsOrderBookResponse, WsSubResponse } from '@/utils/types'
// import type { WsSubRequest } from '@/utils/types'
import { watch, ref, computed, onMounted, useTemplateRef, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { calcDecimals, calcPriceLevel, findMostFrequent } from '@/utils'
import { capitalize } from 'lodash'
import { MAIN_ROUTER_PAGE } from '@/utils/consts'
import { useEyeDexStore } from '@/stores/eyeDexStore'
import EyeHeader from '@/components/EyeHeader.vue'
// import SrDataTable from '@/components/tables/SrDataTable.vue'
// import { getEyeBunchesTableColumns, type Bunch } from '@/columns/eyeBunchesColumns'
import { type Bunch } from '@/columns/eyeBunchesColumns'
import { useNavHeightRefresher } from '@/composable/refreshers/useNavHeightRefresher'

const authStore = useAuthStore()
const commonContract = ref('')
const commonChain = ref('')
const navWrapper = ref<HTMLDivElement>()
const assetsStore = useAssetsStore()
const dexEyeStore = useEyeDexStore()
const themeStore = useThemeStore()
const { navHeight, navHeightRefresher } = useNavHeightRefresher(navWrapper, 61)
const router = useRouter()
const route = useRoute()
const mlw = useTemplateRef('mlw')
const asset = computed(() => route.params.asset as string)
const eyeStore = useEyeStore()
const cexPrices = ref<Record<string, number | null>>({})
const cexPricesTs = ref<Record<string, number | null>>({})
const dexPrice = ref<number | null>(null)
const dexPriceTs = ref<number | null>()
const hiddenSeries = ref<Set<string>>(new Set())
const colorSchemeIcon = computed(() => (themeStore.mode == 'dark' ? 'pi pi-moon' : 'pi pi-sun'))
const amount = ref(100)

const opacityHex = 'aa'
const colors: Record<string, string> = {
  'gate/swap': `#8a0000${opacityHex}`,
  'mexc/swap': `#008a1c${opacityHex}`,
  'bingx/swap': `#008a7a${opacityHex}`,
  'bitget/swap': `#004a8a${opacityHex}`,
  'lbank/swap': `#5a008a${opacityHex}`,
  'bybit/swap': `#8a0088${opacityHex}`,
  'binance/swap': `#8a0050${opacityHex}`,
  'ourbit/swap': `#8a0012${opacityHex}`,
  'blofin/swap': `#8a5a00${opacityHex}`,
  'aster/swap': `#7e8a00${opacityHex}`,
  'hyperliquid/swap': `#008a8a${opacityHex}`,
  gmgn: `#0029af${opacityHex}`,
}

const orderBooks = ref<Record<string, WsOrderBookResponse>>({})
// const columns = getEyeBunchesTableColumns(commonChain, asset, amount)

// Работа с веб-сокетом
const onMessage = async (message: string) => {
  const response: WsSubResponse = JSON.parse(message)
  if (response.event == 'watch') {
    const params = response.params
    if (!params) {
      return
    }
    if (params[0] == 'dex-gmgn-price') {
      const data = response.data as GmgnPriceResponse
      if (data.price) {
        dexPrice.value = Number(data.price)
        dexPriceTs.value = response.ts ?? null
      } else {
        dexPrice.value = null
      }
    } else if (params[0] == 'ticker') {
      const data = response.data as CexTickerResponse
      const exMarket = `${params[1]}/${params[2]}`
      cexPrices.value[exMarket] = data.price
      cexPricesTs.value[exMarket] = response.ts ?? null
    } else if (params[0] == 'ob') {
      const obMarketType = params[1]
      const obEx = params[2]
      if (!obEx || !obMarketType) {
        return
      }
      const key = `${obMarketType}/${obEx}`.toLowerCase()
      orderBooks.value[key] = response.data as WsOrderBookResponse
    }
  }
}

const ticksRefresher = useAutoRefresh(async () => {
  // const tillTs = +new Date() / 1000

  const allTs = Object.entries(cexPricesTs.value)
    .map((x) => x[1])
    .filter((x) => x !== null)
  if (!allTs.length) {
    return
  }
  const maxTs = Math.max(...allTs)
  Object.entries(cexPrices.value).forEach(([x, v]) => {
    mlw.value?.pushIntoSeries(x, [maxTs, v])
  })
  if (dexPrice.value) {
    mlw.value?.pushIntoSeries('gmgn', [maxTs, dexPrice.value])
  }
}, 1000)

// Composable
const { run, stop } = useWebSocket(
  import.meta.env.VITE_WS_URL + '/api/v2/ws?token=' + authStore.token,
  {
    onMessage: onMessage,
    // handleClose: onClose,
    // handleOpen: onOpen,
  },
)

// const chain = computed(() => route.params.chain)

interface Contract {
  chain: string
  address: string
  asset: string
}

const initWs = (exs: [string, Contract][]) => {
  /* Подключаем веб-сокет */
  const subMessages = exs.map((x) => {
    return {
      action: 'watch',
      params: ['ticker', x[0], 'swap', x[1].asset.toLowerCase()],
    }
  })
  subMessages.push(
    ...exs.map((x) => {
      return {
        action: 'watch',
        params: ['ob', 'swap', x[0], x[1].asset.toLowerCase()],
      }
    }),
  )
  if (!contracts.value) {
    return
  }

  subMessages.push({
    action: 'watch',
    params: ['dex-gmgn-price', commonChain.value, commonContract.value],
  })
  const msg = JSON.stringify(subMessages)
  run(msg)
}

const minMove = computed(() => {
  return 1 / Math.pow(10, availableDecimals.value)
})
const contracts = ref<Record<string, Contract>>()
const availableDecimals = ref(12)

const initTicks = async (exchange: string, symbol: string) => {
  const actionId = `ticks/${exchange}/${symbol}`
  const ticks = await eyeStore.fetchSymbolTicks(actionId, exchange, symbol)
  if (ticks) {
    const am = [] as number[]
    ticks.map((x) => calcDecimals(x[1])).forEach((x) => am.push(x))
    availableDecimals.value = Math.max(...am)
    const seriesName = `${exchange}/swap`
    mlw.value?.updateSeries(seriesName, ticks)
  }
}

const initGmgnTicks = async () => {
  const ticks = await dexEyeStore.fetchGmgnTicks('gmgn', commonContract.value, commonChain.value)
  if (ticks) {
    mlw.value?.updateSeries('gmgn', ticks)
  }
}

const bunchesRefresher = useAutoRefresh(async () => {
  bunches.value = updateBunches()
}, 500)


onMounted(async () => {
  navHeightRefresher.startRefresh()
  if (!asset.value) {
    return
  }
  const ctrs = await assetsStore.fetchAssetsContractsByAsset(asset.value, 'swap')
  const exCtrs = (ctrs?.map((x) => {
    return [
      x.exchange_name,
      {
        asset: x.asset_name,
        address: x.contract,
        chain: x.chain_name,
      },
    ]
  }) ?? []) as [string, Contract][]
  contracts.value = Object.fromEntries(exCtrs)

  exCtrs.forEach((v) => {
    const exchange_name = v[0] as string
    const ctr = v[1] as Contract
    if (exchange_name) {
      const seriesName = `${exchange_name}/swap`
      mlw.value?.addSeries(seriesName, colors[seriesName] ?? undefined)
      const symbol = `${ctr.asset}_USDT:USDT`.toUpperCase()
      initTicks(exchange_name, symbol)
    }
  })

  // eslint-disable-next-line
  const ctrChainsOnly = Object.entries(contracts.value).map(([_, v]) => {
    return [v.address.toLocaleLowerCase(), v.chain]
  })
  const mf = findMostFrequent(ctrChainsOnly)
  if (!mf) {
    return
  }
  commonContract.value = mf[0] as string
  commonChain.value = mf[1] as string

  mlw.value?.addSeries('gmgn', colors['gmgn'] ?? undefined)
  initGmgnTicks()
  initWs(exCtrs)
  bunchesRefresher.startRefresh()
  ticksRefresher.startRefresh()
})

onUnmounted(() => {
  navHeightRefresher.stopRefresh()
  stop()
})

const toggleSeries = (name: string) => {
  if (hiddenSeries.value.has(name)) {
    hiddenSeries.value.delete(name)
    mlw.value?.setSeriesVisibility(name, true)
  } else {
    hiddenSeries.value.add(name)
    mlw.value?.setSeriesVisibility(name, false)
  }
}

const getBtnLabel = (v: string) => {
  if (v == 'gmgn') {
    return 'Dex'
  }
  return capitalize(v).split('/')[0]
}


const bunches = ref<Bunch[]>([])

const updateBunches = () => {
  if (!contracts.value) {
    return []
  }
  const exs = Object.entries(contracts.value).map(([x]) => {
    const obKey = `swap/${x}`.toLowerCase()
    const ob = orderBooks.value[obKey]
    const buyData = ob?.asks ? calcPriceLevel(ob?.asks, amount.value, 1) : null
    const sellData = ob?.bids ? calcPriceLevel(ob?.bids, amount.value, 1) : null

    const spreadLong = buyData && dexPrice.value ? dexPrice.value / buyData[0] : null
    const spreadShort = sellData && dexPrice.value ? sellData[0] / dexPrice.value : null

    return {
      exchange: x,
      long: spreadLong ? Number(spreadLong).toFixed(2) : '—',
      short: spreadShort ? Number(spreadShort).toFixed(2) : '—',
    }
  }).filter(x => {
    return x.long !== '—' && x.short !== '—'
  })

  return exs as Bunch[]
}


watch(
  () => authStore.currentAccessList,
  (v) => {
    if (!('dex_cex' in v || 'dex_cex_advanced' in v || 'dex_cex_free' in v)) {
      themeStore.forbidden = true
      return
    }
  },
)

</script>

<template>
  <div>
    <div ref="navWrapper" class="sr-navbar-container">
      <EyeHeader :hideSpreads="true">
        <template #symbol>
          <span class="uppercase pit">{{ asset.toUpperCase() }}</span>
        </template>
        <template #end>
          <div class="sr-eye-navbar-end gap-1 flex items-center">
            <Button v-tooltip.bottom="$t('goHome')" icon="pi pi-home" @click="router.push(MAIN_ROUTER_PAGE)"
              variant="text" rounded aria-label="Home" size="small" />
            <Button v-tooltip.bottom="$t('changeColorScheme')" :icon="colorSchemeIcon" @click="themeStore.toggle()"
              variant="text" rounded aria-label="Theme" size="small" />
          </div>
        </template>
      </EyeHeader>
    </div>
    <main class="mx-0 mt-0" :style="{ paddingTop: navHeight + 'px' }">
      <Card pt:body:class="p-0" pt:root:class="rounded-none lg:col-span-2" style="border: none;">
        <template #content>
          <MLwChart y-format="1s" height="600px" ref="mlw" :min-move="minMove"></MLwChart>
          <!-- Выбор бирж -->
          <div
            class="grid grid-cols-2 md:grid-cols-6 lg:grid-cols-10 2xl:grid-cols-14 gap-3 mt-4 px-2 pb-2 border-t-1 border-color pt-2"
            style="background-color: var(--sr-navbar-bg-color)">
            <div v-for="key in mlw?.availableSeries" :key="key" class="w-full">
              <div @click="toggleSeries(key)" class="pit hover cursor-pointer w-full flex items-center justify-between"
                :class="{ 'success': !hiddenSeries.has(key), 'danger': hiddenSeries.has(key) }">
                <div class="square ms-1" :style="`background: ${colors[key] ?? 'transparent'}`"></div>
                {{ getBtnLabel(key) }}
                <i :class="hiddenSeries.has(key) ? 'pi pi-stop' : 'pi pi-check-square'" :label="getBtnLabel(key)"></i>
              </div>
            </div>
          </div>
        </template>
      </Card>

      <!-- <Card pt:body:class="p-0" pt:root:class="rounded-none lg:col-span-2"
        style="border-left: none;border-right: none;">
        <template #header>
          <div class="px-3 pb-3 pt-2 border-b-1 border-color">
            <span class="pit success font-bold">{{ $t('bunchesDexCex') }}</span>
          </div>
        </template>
        <template #content>
          <SrDataTable :loading="bunches.length == 0" skeleton-height="20px" :fake-rows-count="7" size="small"
            :columns="columns" :value="bunches"></SrDataTable>
        </template>
      </Card> -->
    </main>
  </div>
</template>

<style scoped>
.square {
  height: 14px;
  width: 14px;
}
</style>
